import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  Activity,
  Search,
  Filter,
  Calendar,
  User,
  CheckCircle,
  XCircle,
  Clock,
  Monitor,
  AlertTriangle,
  FileText,
  ChevronDown,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  fetchActivityLogs,
  setFilters,
  clearFilters,
  setPage,
  clearError,
} from "../../RTK/activityLogs/GetActivityLogs";
import Loader from "../../components/loader/Loader";

const ActivityLogs = () => {
  const dispatch = useDispatch();
  const { logs, loading, error, pagination, filters } = useSelector(
    (state) => state.activityLogs
  );

  const [showFilters, setShowFilters] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);
  const [expandedLog, setExpandedLog] = useState(null);

  useEffect(() => {
    dispatch(fetchActivityLogs({ ...filters, page: pagination.page }));
  }, [dispatch, filters, pagination.page]);

  useEffect(() => {
    if (error) {
      setTimeout(() => dispatch(clearError()), 5000);
    }
  }, [error, dispatch]);

  const handleFilterChange = (key, value) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    dispatch(setFilters(localFilters));
    dispatch(setPage(1));
  };

  const clearAllFilters = () => {
    setLocalFilters({
      user_id: "",
      action: "",
      from_date: "",
      to_date: "",
    });
    dispatch(clearFilters());
    dispatch(setPage(1));
  };

  const refreshLogs = () => {
    dispatch(fetchActivityLogs({ ...filters, page: pagination.page }));
  };

  const handlePageChange = (page) => {
    dispatch(setPage(page));
  };

  const getActionIcon = (action, success) => {
    if (!success) return <XCircle className="w-5 h-5 text-red-400" />;
    
    switch (action) {
      case "إنشاء":
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case "تحديث":
        return <RefreshCw className="w-5 h-5 text-blue-400" />;
      case "حذف":
        return <XCircle className="w-5 h-5 text-red-400" />;
      default:
        return <Activity className="w-5 h-5 text-purple-400" />;
    }
  };

  const getActionColor = (action, success) => {
    if (!success) return "bg-red-900/20 border-red-500/30";
    
    switch (action) {
      case "إنشاء":
        return "bg-green-900/20 border-green-500/30";
      case "تحديث":
        return "bg-blue-900/20 border-blue-500/30";
      case "حذف":
        return "bg-red-900/20 border-red-500/30";
      default:
        return "bg-purple-900/20 border-purple-500/30";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("ar-EG", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderPaginationNumbers = () => {
    const totalPages = Math.ceil(pagination.total_count / pagination.page_size);
    const currentPage = pagination.page;
    const pages = [];

    // إذا كان العدد الكلي للصفحات 7 أو أقل، اعرض كل الصفحات
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // إذا كانت الصفحة الحالية في البداية
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, '...', totalPages);
      }
      // إذا كانت الصفحة الحالية في النهاية
      else if (currentPage >= totalPages - 3) {
        pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      }
      // إذا كانت الصفحة الحالية في المنتصف
      else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }

    return pages.map((page, index) => {
      if (page === '...') {
        return (
          <span
            key={`ellipsis-${index}`}
            className="px-3 py-2 text-slate-400 flex items-center"
          >
            ...
          </span>
        );
      }

      return (
        <button
          key={page}
          onClick={() => handlePageChange(page)}
          className={`px-3 py-2 rounded-xl transition-colors min-w-[40px] flex items-center justify-center ${
            currentPage === page
              ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg"
              : "bg-slate-800/50 backdrop-blur-sm border border-slate-700 text-white hover:bg-slate-700/50"
          }`}
        >
          {page}
        </button>
      );
    });
  };

  const actionTypes = ["إنشاء", "تحديث", "حذف", "عرض", "تسجيل دخول", "تسجيل خروج"];

  if (loading && logs.length === 0) {
    return <Loader />;
  }

  return (
    <div className="min-h-screen bg-[#0f172a] p-4 md:p-6" dir="rtl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-r from-orange-600 to-red-600 p-3 rounded-xl shadow-lg">
                <Activity className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">سجل الأنشطة</h1>
                <p className="text-slate-400">
                  عرض جميع الأنشطة والعمليات ({pagination.total_count} سجل)
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={refreshLogs}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 backdrop-blur-sm border border-slate-700 text-white rounded-xl hover:bg-slate-700/50 transition-colors disabled:opacity-50"
              >
                <RefreshCw
                  className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                />
                تحديث
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors ${
                  showFilters
                    ? "bg-blue-600 text-white"
                    : "bg-slate-800/50 backdrop-blur-sm border border-slate-700 text-white hover:bg-slate-700/50"
                }`}
              >
                <Filter className="w-4 h-4" />
                الفلاتر
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    showFilters ? "rotate-180" : ""
                  }`}
                />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Filters */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 mb-8"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  معرف المستخدم
                </label>
                <input
                  type="text"
                  placeholder="معرف المستخدم"
                  value={localFilters.user_id}
                  onChange={(e) => handleFilterChange("user_id", e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  نوع العملية
                </label>
                <select
                  value={localFilters.action}
                  onChange={(e) => handleFilterChange("action", e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">جميع العمليات</option>
                  {actionTypes.map((action) => (
                    <option key={action} value={action}>
                      {action}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  من تاريخ
                </label>
                <input
                  type="date"
                  value={localFilters.from_date}
                  onChange={(e) =>
                    handleFilterChange("from_date", e.target.value)
                  }
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  إلى تاريخ
                </label>
                <input
                  type="date"
                  value={localFilters.to_date}
                  onChange={(e) => handleFilterChange("to_date", e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={applyFilters}
                className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg"
              >
                <Search className="w-4 h-4" />
                تطبيق الفلاتر
              </button>
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-2 px-6 py-2 bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-600/50 transition-colors"
              >
                <XCircle className="w-4 h-4" />
                مسح الفلاتر
              </button>
            </div>
          </motion.div>
        )}

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-red-900/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl mb-6 backdrop-blur-sm"
          >
            {error}
          </motion.div>
        )}

        {/* Logs List */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="space-y-4 mb-8"
        >
          {logs.map((log, index) => (
            <LogCard
              key={log.id}
              log={log}
              index={index}
              isExpanded={expandedLog === log.id}
              onToggleExpand={() =>
                setExpandedLog(expandedLog === log.id ? null : log.id)
              }
              getActionIcon={getActionIcon}
              getActionColor={getActionColor}
              formatDate={formatDate}
            />
          ))}
        </motion.div>

        {/* Empty State */}
        {!loading && logs.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-12"
          >
            <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-400 mb-2">
              لا توجد سجلات
            </h3>
            <p className="text-slate-500">
              لم يتم العثور على سجلات أنشطة مطابقة للفلاتر المحددة
            </p>
          </motion.div>
        )}

        {/* Improved Pagination */}
        {pagination.total_count > pagination.page_size && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {/* Results Info */}
            <div className="text-center text-sm text-slate-400">
              عرض {((pagination.page - 1) * pagination.page_size) + 1} - {Math.min(pagination.page * pagination.page_size, pagination.total_count)} من {pagination.total_count} سجل
            </div>

            {/* Pagination Controls */}
            <div className="flex justify-center items-center gap-2 flex-wrap">
              {/* First Page */}
              <button
                onClick={() => handlePageChange(1)}
                disabled={pagination.page === 1}
                className="px-3 py-2 bg-slate-900 backdrop-blur-sm border border-slate-700 text-white rounded-xl hover:bg-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
              >
                <ChevronRight className="w-4 h-4" />
                الأولى
              </button>

              {/* Previous Button */}
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.has_previous}
                className="px-4 py-2 bg-slate-800/50 backdrop-blur-sm border border-slate-700 text-white rounded-xl hover:bg-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
              >
                <ChevronRight className="w-4 h-4" />
                السابق
              </button>

              {/* Page Numbers */}
              <div className="flex gap-1 flex-wrap justify-center">
                {renderPaginationNumbers()}
              </div>

              {/* Next Button */}
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.has_next}
                className="px-4 py-2 bg-slate-800/50 backdrop-blur-sm border border-slate-700 text-white rounded-xl hover:bg-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
              >
                التالي
                <ChevronLeft className="w-4 h-4" />
              </button>

              {/* Last Page */}
              <button
                onClick={() => handlePageChange(Math.ceil(pagination.total_count / pagination.page_size))}
                disabled={pagination.page === Math.ceil(pagination.total_count / pagination.page_size)}
                className="px-3 py-2 bg-slate-900 backdrop-blur-sm border border-slate-700 text-white rounded-xl hover:bg-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
              >
                الأخيرة
                <ChevronLeft className="w-4 h-4" />
              </button>
            </div>

            {/* Current Page Info */}
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900/50 rounded-xl border border-slate-700">
                <span className="text-slate-400">الصفحة</span>
                <span className="text-white font-medium">{pagination.page}</span>
                <span className="text-slate-400">من</span>
                <span className="text-white font-medium">
                  {Math.ceil(pagination.total_count / pagination.page_size)}
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

// Log Card Component
const LogCard = ({
  log,
  index,
  isExpanded,
  onToggleExpand,
  getActionIcon,
  getActionColor,
  formatDate,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden hover:border-blue-500/30 transition-all duration-200 ${getActionColor(
        log.action,
        log.success
      )}`}
    >
      <div
        className="p-6 cursor-pointer"
        onClick={onToggleExpand}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4 flex-1">
            <div className="mt-1">{getActionIcon(log.action, log.success)}</div>
            
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="font-semibold text-white">{log.action}</h3>
                <span className="px-2 py-1 bg-slate-900/50 text-slate-300 text-xs rounded-full">
                  {log.model_name}
                </span>
                {!log.success && (
                  <span className="px-2 py-1 bg-red-900/50 text-red-300 text-xs rounded-full">
                    فشل
                  </span>
                )}
              </div>
              
              <p className="text-slate-300 mb-2">{log.description}</p>
              <p className="text-sm text-slate-400">{log.object_repr}</p>
              
              <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                <div className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {log.user.username}
                </div>
                <div className="flex items-center gap-1">
                  <Monitor className="w-3 h-3" />
                  {log.ip_address}
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatDate(log.created_at)}
                </div>
              </div>
            </div>
          </div>
          
          <ChevronDown
            className={`w-5 h-5 text-slate-400 transition-transform ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="border-t border-slate-700/50 p-6 bg-slate-900/30"
        >
          {log.error_message && (
            <div className="mb-4 p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="font-medium text-red-300">رسالة الخطأ</span>
              </div>
              <p className="text-red-200 text-sm">{log.error_message}</p>
            </div>
          )}

          {log.extra_data && Object.keys(log.extra_data).length > 0 && (
            <div>
              <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                تفاصيل إضافية
              </h4>
              <div className="space-y-2">
                {Object.entries(log.extra_data).map(([key, value]) => (
                  <div
                    key={key}
                    className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg"
                  >
                    <span className="text-slate-400 font-medium min-w-0 flex-shrink-0">
                      {key}:
                    </span>
                    <span className="text-slate-200 break-all">
                      {Array.isArray(value)
                        ? value.join(", ")
                        : typeof value === "object"
                        ? JSON.stringify(value, null, 2)
                        : String(value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

export default ActivityLogs;